import { FaAngleDown } from "react-icons/fa";
import { NoShowModal } from "./NoShowModal";
import { NoShowNotificationModal } from "./NoShowNotificationModal";
import { AppointmentCancelModal } from "./AppointmentCancelModal";
import { useDisclosure } from "@mantine/hooks";
import { GetAppointment } from "@/server-types";
import { Trans } from "@lingui/react/macro";
import { useAppointmentModalStore } from "@/Components/AppointmentModal/useAppointmentModalStore_";
import { useCalendarStore } from "../../../store/useCalendarStore";
import { api } from "@/lib/api-client";
import useUserPermissionsStore from "@/stores/useUserPermissionsStore";
import dayjs from "dayjs";
import { t } from "@lingui/core/macro";
import {
  addToast,
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
} from "@heroui/react";

const getAppointmentState = (appointment: Appointment) => {
  const currentTime = dayjs();
  const startTime = dayjs(appointment.startTime);
  const totalDuration = appointment.appointmentParts.reduce(
    (total, part) => total + part.duration,
    0,
  );
  const endTime = startTime.add(totalDuration, "minute");

  if (
    appointment.customers.length === 1 &&
    appointment.customers[0]?.customerDidAttend === -1
  ) {
    return { title: t`Neprihod`, color: "orange" };
  }
  if (currentTime.isBefore(startTime)) {
    return { title: t`Rezerviran`, color: "green" };
  }
  if (currentTime.isAfter(endTime)) {
    return { title: t`Zaključen`, color: "blue" };
  }
  return { title: t`V teku`, color: "teal" };
};

type CustomerAppointments = {
  customers: {
    name: string;
    lastName: string;
    userAppointmentId: number;
    customerDidAttend: number;
    isDeleted?: boolean;
    paymentIntentId?: string | null;
  }[];
};

export type Appointment = GetAppointment["response"]["appointment"] &
  CustomerAppointments;

const AppointmentActionsDropdown = ({
  appointment,
  selectedAppointmentId,
}: {
  appointment: Appointment;
  selectedAppointmentId: number;
}) => {
  const [
    isNoShowModalOpen,
    { open: openNoShowModal, close: closeNoShowModal },
  ] = useDisclosure(false);
  const [
    isNoShowNotificationModalOpen,
    { open: openNoShowNotificationModal, close: closeNoShowNotificationModal },
  ] = useDisclosure(false);
  const [
    isCancelModalOpen,
    { open: openCancelModal, close: closeCancelModal },
  ] = useDisclosure(false);

  const canDeleteAppointments = useUserPermissionsStore(
    (state: { permissions: { restrict_appointment_delete?: boolean } }) =>
      !state.permissions.restrict_appointment_delete,
  );

  const hasNoShowNotification = useCalendarStore(
    (state) => state.hasNoShowNotification,
  );

  const appointmentState = getAppointmentState(appointment);

  const { fetchAppointmentData: refreshAppointmentData } =
    useAppointmentModalStore((state) => state);

  const { mutateAsync: updateCustomerAppointments } =
    api.customerAppointment.useUpdateCustomerAppointmentsAttendance();

  const handleUpdateCustomerAppointments = async (
    customerAppointments: {
      userAppointmentId: number;
      customerDidAttend: number;
    }[],
    options?: { sendNoShowNotification?: boolean },
  ) => {
    try {
      await updateCustomerAppointments({
        body: {
          updates: customerAppointments,
          sendNoShowNotification: options?.sendNoShowNotification,
        },
      });

      addToast({
        title: t`Uspešno`,
        description: t`Prisotnost je bila uspešno posodobljena.`,
        color: "success",
      });
    } catch (error) {
      addToast({
        title: t`Napaka`,
        description: t`Prisotnosti ni bilo mogoče posodobiti. Poskusite znova.`,
        color: "danger",
      });
    }
    refreshAppointmentData();
    useCalendarStore.getState().refreshCalendar();
  };

  const firstCustomer = appointment.customers[0];
  const hasCustomers = appointment.customers.length > 0;

  return (
    <>
      <Dropdown className="w-[200px] rounded-lg">
        <DropdownTrigger>
          <Button
            variant="bordered"
            style={{
              color: appointmentState.color,
              borderColor: appointmentState.color,
            }}
            size="sm"
            data-identifier="appointment-actions-dropdown"
          >
            <div className="flex items-center gap-2">
              {appointmentState.title}
              <FaAngleDown
                style={{
                  marginTop: 2,
                }}
              />
            </div>
          </Button>
        </DropdownTrigger>

        <DropdownMenu
          disabledKeys={!hasCustomers ? ["mark-attendance-item"] : []}
        >
          <DropdownItem
            key="mark-attendance-item"
            data-identifier="mark-attendance-item"
            color="warning"
            variant="flat"
            onPress={() => {
              if (!firstCustomer) return;

              if (appointment.isPreScheduled) {
                openNoShowModal();
                return;
              }

              if (firstCustomer.customerDidAttend !== -1) {
                openNoShowNotificationModal();
                return;
              }

              handleUpdateCustomerAppointments([
                {
                  userAppointmentId: firstCustomer.userAppointmentId,
                  customerDidAttend: 1,
                },
              ]);
            }}
          >
            {appointment.isPreScheduled ? (
              <Trans>Označi prisotnost</Trans>
            ) : (
              <>
                {firstCustomer?.customerDidAttend === -1
                  ? t`Razveljavi neprihod`
                  : t`Neprihod`}
              </>
            )}
          </DropdownItem>
          {canDeleteAppointments ? (
            <DropdownItem
              key="cancel-appointment-item"
              color="danger"
              onPress={openCancelModal}
              data-identifier="cancel-appointment-item"
              variant="flat"
            >
              <Trans>Odpoved termina</Trans>
            </DropdownItem>
          ) : null}
        </DropdownMenu>
      </Dropdown>

      {appointment.isPreScheduled && (
        <NoShowModal
          handleUpdateCustomerAppointments={handleUpdateCustomerAppointments}
          isOpen={isNoShowModalOpen}
          onClose={closeNoShowModal}
          appointment={appointment}
        />
      )}

      {!appointment.isPreScheduled && firstCustomer && (
        <NoShowNotificationModal
          isOpen={isNoShowNotificationModalOpen}
          onClose={closeNoShowNotificationModal}
          hasNoShowNotification={hasNoShowNotification}
          startTime={appointment.startTime}
          customers={[
            {
              key: firstCustomer.userAppointmentId,
              name: firstCustomer.name,
              lastName: firstCustomer.lastName,
            },
          ]}
          selectedServices={appointment.appointmentParts.map((part) => ({
            serviceId: part.serviceId,
          }))}
          locationId={appointment.locationId}
          onConfirm={(sendNoShowNotification) =>
            handleUpdateCustomerAppointments(
              [
                {
                  userAppointmentId: firstCustomer.userAppointmentId,
                  customerDidAttend: -1,
                },
              ],
              { sendNoShowNotification },
            )
          }
        />
      )}

      <AppointmentCancelModal
        isOpen={isCancelModalOpen}
        onClose={closeCancelModal}
        appointmentId={selectedAppointmentId}
        userAppointmentId={firstCustomer?.userAppointmentId}
        isRepeating={appointment.repeatingAppointment}
        isLinked={appointment.linkedAppointment}
        paymentIntentId={firstCustomer?.paymentIntentId}
      />
    </>
  );
};

export default AppointmentActionsDropdown;
